import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import ColorPickerPopover from './ColorPickerPopover';

const FONT_FAMILIES = [
  'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat',
  'Poppins', 'Source Sans Pro', 'Nunito', 'Raleway', 'Georgia',
  'serif', 'sans-serif', 'monospace',
];

const FONT_WEIGHTS = [
  { value: '400', label: 'Regular' },
  { value: '500', label: 'Medium' },
  { value: '600', label: 'SemiBold' },
  { value: '700', label: 'Bold' },
  { value: '800', label: 'ExtraBold' },
];

const Row = ({ label, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
    <Typography variant="caption" sx={{ color: '#888', fontSize: 11, width: 56, flexShrink: 0 }}>
      {label}
    </Typography>
    {children}
  </Box>
);

const NumInput = ({ value, onChange, unit = 'px', min = 0 }) => {
  const num = parseFloat(value) || 0;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <TextField
        size="small"
        type="number"
        value={num}
        onChange={(e) => onChange(e.target.value + unit)}
        inputProps={{ min, style: { fontSize: 12, padding: '3px 6px', width: 48 } }}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
      />
      <Typography variant="caption" sx={{ color: '#aaa', fontSize: 10 }}>{unit}</Typography>
    </Box>
  );
};

const TypographyPanel = ({ styles = {}, onChange }) => {
  const [colorAnchor, setColorAnchor] = useState(null);

  const get = (prop, fallback = '') => styles[prop] || fallback;

  const colorValue = get('color', '#000000');
  const hexColor = colorValue.startsWith('rgb') ? '#000000' : colorValue;

  return (
    <Box>
      {/* Font family */}
      <Row label="Family">
        <Select
          size="small"
          value={get('fontFamily', 'Inter')}
          onChange={(e) => onChange('fontFamily', e.target.value)}
          sx={{ fontSize: 12, flex: 1, '& .MuiSelect-select': { py: 0.5 } }}
        >
          {FONT_FAMILIES.map((f) => (
            <MenuItem key={f} value={f} sx={{ fontSize: 12, fontFamily: f }}>
              {f}
            </MenuItem>
          ))}
        </Select>
      </Row>

      {/* Size + Weight */}
      <Row label="Size / Wt">
        <NumInput
          value={get('fontSize', '16px')}
          onChange={(v) => onChange('fontSize', v)}
        />
        <Select
          size="small"
          value={get('fontWeight', '400')}
          onChange={(e) => onChange('fontWeight', e.target.value)}
          sx={{ fontSize: 12, flex: 1, '& .MuiSelect-select': { py: 0.5 } }}
        >
          {FONT_WEIGHTS.map((w) => (
            <MenuItem key={w.value} value={w.value} sx={{ fontSize: 12 }}>
              {w.label}
            </MenuItem>
          ))}
        </Select>
      </Row>

      {/* Color */}
      <Row label="Color">
        <Box
          onClick={(e) => setColorAnchor(e.currentTarget)}
          sx={{
            width: 24,
            height: 24,
            borderRadius: 1,
            background: hexColor,
            border: '1px solid #ddd',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        />
        <TextField
          size="small"
          value={hexColor}
          onChange={(e) => onChange('color', e.target.value)}
          inputProps={{ style: { fontSize: 12, padding: '3px 6px', fontFamily: 'monospace' } }}
          sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
        />
        <ColorPickerPopover
          anchorEl={colorAnchor}
          open={Boolean(colorAnchor)}
          onClose={() => setColorAnchor(null)}
          value={hexColor}
          onChange={(c) => { onChange('color', c); }}
        />
      </Row>

      {/* Align */}
      <Row label="Align">
        <ToggleButtonGroup
          size="small"
          exclusive
          value={get('textAlign', 'left')}
          onChange={(_, v) => v && onChange('textAlign', v)}
        >
          {[
            { value: 'left', icon: <FormatAlignLeftIcon sx={{ fontSize: 14 }} /> },
            { value: 'center', icon: <FormatAlignCenterIcon sx={{ fontSize: 14 }} /> },
            { value: 'right', icon: <FormatAlignRightIcon sx={{ fontSize: 14 }} /> },
          ].map((a) => (
            <ToggleButton key={a.value} value={a.value} sx={{ p: 0.5 }}>
              {a.icon}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <ToggleButtonGroup
          size="small"
          value={get('fontStyle', 'normal') === 'italic' ? ['italic'] : []}
          onChange={(_, v) => onChange('fontStyle', v.includes('italic') ? 'italic' : 'normal')}
        >
          <ToggleButton value="italic" sx={{ p: 0.5 }}>
            <FormatItalicIcon sx={{ fontSize: 14 }} />
          </ToggleButton>
        </ToggleButtonGroup>
      </Row>

      {/* Line height + Letter spacing */}
      <Row label="Line / LS">
        <NumInput
          value={get('lineHeight', '1.5')}
          onChange={(v) => onChange('lineHeight', v)}
          unit=""
        />
        <NumInput
          value={get('letterSpacing', '0px')}
          onChange={(v) => onChange('letterSpacing', v)}
        />
      </Row>
    </Box>
  );
};

export default TypographyPanel;
